/**
 * Hiusextra: tuotesivun värivalinta v2.3 (snippets/hx-color-picker.liquid, snippets/color-drawer.liquid).
 *
 * - Sävypainike valitsee vastaavan natiivin radio-inputin ja lähettää change-tapahtuman, jolloin Horizonin
 *   variant-picker hoitaa variantin, URL:n (?variant=), kuvan ja hinnan ja renderöi rivin uudelleen.
 * - Hover-esikatselu: sävyn nimi vaihtuu hoverattuun ja palaa valittuun, kun hiiri poistuu riviltä.
 * - Sävypaneeli: piilotettu lähde kloonataan avattaessa <body>-elementtiin (pikaostoksessa dialogiin), jotta
 *   Horizonin uudelleenrenderöinti ei sulje sitä. Esc ja taustaklikkaus sulkevat, fokus pysyy paneelissa,
 *   sivun vieritys lukitaan. Paneelin valinta päivittää paneelin heti selaimessa.
 * - Mobiilissa värin valinta vierittää tuotekuvaan (rivistä heti, paneelista sen sulkeutuessa), jotta valittu
 *   väri näkyy isona.
 */
(function () {
  if (window.hxColorPickerReady) return;
  window.hxColorPickerReady = true;

  /** @type {{ root: HTMLElement, picker: HTMLElement, opener: HTMLElement | null, changed?: boolean } | null} */
  let current = null;

  const mobile = window.matchMedia('(max-width: 749px)');

  /**
   * Mobiili: vieritä tuotteen kuvagalleriaan headerin alle.
   * @param {HTMLElement} picker
   */
  function scrollToMedia(picker) {
    if (!mobile.matches || picker.closest('dialog')) return;
    const section = picker.closest('.shopify-section') || document;
    const media = section.querySelector('media-gallery, .product-information__media');
    if (!(media instanceof HTMLElement)) return;
    const header = document.querySelector('#header-component');
    const offset = (header instanceof HTMLElement ? header.getBoundingClientRect().height : 0) + 8;
    const top = media.getBoundingClientRect().top + window.scrollY - offset;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
  }

  const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

  /** @param {Element} el */
  const pickerOf = (el) => /** @type {HTMLElement | null} */ (el.closest('variant-picker'));

  /**
   * Valitsee natiivin inputin (Horizonin logiikka hoitaa loput).
   * @param {HTMLElement} picker
   * @param {string} valueId
   */
  function selectValue(picker, valueId) {
    const input = /** @type {HTMLInputElement | null} */ (
      picker.querySelector(`[data-hx-color] input[data-option-value-id="${valueId}"]`)
    );
    if (!input || input.checked) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Päivittää rivin heti (ennen Horizonin uudelleenrenderöintiä).
   * @param {HTMLElement} picker
   * @param {string} valueId
   * @param {string} name
   */
  function markRow(picker, valueId, name) {
    picker.querySelectorAll('[data-hx-color-swatch]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-option-value-id') === valueId));
    });
    const label = picker.querySelector('[data-hx-color-name]');
    if (label instanceof HTMLElement) {
      label.dataset.selected = name;
      label.textContent = name;
    }
  }

  /**
   * Päivittää paneelin valitun sävyn (kortit, alaosan nimi, oppaan kuva ja värinapit).
   * @param {HTMLElement} root
   * @param {string} valueId
   */
  function markDrawer(root, valueId) {
    /** @type {HTMLElement | null} */
    let selectedCard = null;
    root.querySelectorAll('[data-hx-color-card]').forEach((card) => {
      const isSelected = card.getAttribute('data-option-value-id') === valueId;
      card.setAttribute('aria-pressed', String(isSelected));
      if (isSelected && card instanceof HTMLElement) selectedCard = card;
    });
    if (!selectedCard) return;
    const card = /** @type {HTMLElement} */ (selectedCard);

    const name = root.querySelector('[data-hx-color-selected-name]');
    if (name) name.textContent = card.dataset.name || '';

    const image = root.querySelector('[data-hx-color-guide-image] img');
    if (image instanceof HTMLImageElement && card.dataset.image) {
      image.removeAttribute('srcset');
      image.src = card.dataset.image;
    }

    let hasColors = false;
    for (const part of ['tyvi', 'pituudet', 'latvat']) {
      const dot = root.querySelector(`[data-hx-color-dot="${part}"]`);
      const color = card.dataset[part] || '';
      if (!(dot instanceof HTMLElement)) continue;
      dot.hidden = !color;
      if (color) {
        dot.style.setProperty('--dot', color);
        hasColors = true;
      }
    }
    root.querySelector('[data-hx-color-guide-parts]')?.classList.toggle('hx-color-guide__part-list--no-colors', !hasColors);
    // Tyvi / pituudet / latvat -rivit näkyvät, kun selityksiä on tai sävyllä on värinapit (kuva näkyy aina)
    const partList = root.querySelector('[data-hx-color-guide-parts]');
    if (partList instanceof HTMLElement) partList.hidden = !hasColors && partList.dataset.hasText !== 'true';
  }

  /**
   * @param {HTMLElement} root
   * @param {'colors' | 'guide'} view
   */
  function showView(root, view) {
    root.querySelectorAll('[data-hx-color-view]').forEach((section) => {
      if (section instanceof HTMLElement) section.hidden = section.dataset.hxColorView !== view;
    });
    const panel = root.querySelector('[data-hx-color-panel]');
    const title = root.querySelector(`[data-hx-color-view="${view}"] [data-hx-color-title]`);
    if (panel && title) panel.setAttribute('aria-labelledby', title.id);
    const body = root.querySelector(`[data-hx-color-view="${view}"] .hx-color-drawer__body`);
    if (body) body.scrollTop = 0;
    if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
  }

  /**
   * @param {HTMLElement} picker
   * @param {'colors' | 'guide'} view
   * @param {HTMLElement} opener
   */
  function openDrawer(picker, view, opener) {
    const source = picker.querySelector('[data-hx-color-drawer]');
    if (!(source instanceof HTMLElement)) return;
    if (current) closeDrawer(false);

    const root = /** @type {HTMLElement} */ (source.cloneNode(true));
    // Lähteen id:t ovat jo sivulla: paneelin otsikoille omat id:t
    root.querySelectorAll('[data-hx-color-title]').forEach((title, index) => {
      title.id = `HxColorDrawerTitle-live-${index}`;
    });
    root.classList.add('hx-color-drawer--live');
    root.hidden = false;
    // Lähteen kuvat ovat laiskoja (piilossa ei ladata); auki olevassa paneelissa ladataan heti
    root.querySelectorAll('img[loading="lazy"]').forEach((img) => img.setAttribute('loading', 'eager'));

    const container = picker.closest('dialog') || document.body;
    container.appendChild(root);

    const checked = picker.querySelector('[data-hx-color] input:checked');
    if (checked instanceof HTMLInputElement && checked.dataset.optionValueId) {
      markDrawer(root, checked.dataset.optionValueId);
    }

    current = { root, picker, opener };
    document.documentElement.classList.add('hx-color-drawer-open');
    showView(root, view);

    requestAnimationFrame(() => requestAnimationFrame(() => root.classList.add('is-open')));
  }

  /** @param {boolean} [restoreFocus] */
  function closeDrawer(restoreFocus = true) {
    if (!current) return;
    const { root, opener, picker, changed } = current;
    current = null;
    document.documentElement.classList.remove('hx-color-drawer-open');
    root.classList.remove('is-open');

    const remove = () => root.remove();
    const panel = root.querySelector('[data-hx-color-panel]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (panel && !reduce) {
      panel.addEventListener('transitionend', remove, { once: true });
      setTimeout(remove, 600);
    } else {
      remove();
    }

    if (restoreFocus && opener && opener.isConnected) opener.focus({ preventScroll: true });
    if (changed) requestAnimationFrame(() => scrollToMedia(picker));
  }

  // ---------- Sticky-ostopalkki: kuva / nimi / sävy avaa sävypaneelin ----------
  const STICKY_TARGET = 'sticky-add-to-cart .sticky-add-to-cart__info, sticky-add-to-cart .sticky-add-to-cart__image';

  /** Tuotesivun värivalinta (ei pikaostoksen dialogissa) */
  function mainColorPicker() {
    const fieldset = Array.from(document.querySelectorAll('variant-picker[data-template-product-match="true"] [data-hx-color]')).find(
      (el) => !el.closest('dialog')
    );
    return fieldset ? pickerOf(fieldset) : null;
  }

  function setupSticky() {
    if (!mainColorPicker()) return;
    document.documentElement.classList.add('hx-sticky-color');
    document.querySelectorAll('sticky-add-to-cart .sticky-add-to-cart__info').forEach((info) => {
      info.setAttribute('role', 'button');
      info.setAttribute('tabindex', '0');
      info.setAttribute('aria-haspopup', 'dialog');
      info.setAttribute('aria-label', 'Vaihda väri');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupSticky);
  } else {
    setupSticky();
  }

  /** @param {Element} target */
  function openFromSticky(target) {
    const trigger = target.closest(STICKY_TARGET);
    if (!trigger) return false;
    const picker = mainColorPicker();
    if (!picker) return false;
    const info = trigger.closest('sticky-add-to-cart')?.querySelector('.sticky-add-to-cart__info');
    openDrawer(picker, 'colors', info instanceof HTMLElement ? info : /** @type {HTMLElement} */ (trigger));
    return true;
  }

  document.addEventListener('keydown', (event) => {
    if (current || (event.key !== 'Enter' && event.key !== ' ')) return;
    const target = event.target instanceof Element ? event.target : null;
    if (target?.matches('sticky-add-to-cart .sticky-add-to-cart__info') && openFromSticky(target)) {
      event.preventDefault();
    }
  });

  // ---------- Rivi ----------
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (!current && openFromSticky(target)) return;

    const swatch = target.closest('[data-hx-color-swatch]');
    if (swatch instanceof HTMLButtonElement && !swatch.disabled) {
      const picker = pickerOf(swatch);
      const valueId = swatch.dataset.optionValueId;
      if (!picker || !valueId) return;
      markRow(picker, valueId, swatch.dataset.name || '');
      selectValue(picker, valueId);
      scrollToMedia(picker);
      return;
    }

    const opener = target.closest('[data-hx-color-open]');
    if (opener instanceof HTMLElement && !opener.closest('[data-hx-color-drawer]')) {
      const picker = pickerOf(opener);
      if (!picker) return;
      openDrawer(picker, opener.dataset.hxColorOpen === 'guide' ? 'guide' : 'colors', opener);
      return;
    }

    // ---------- Paneeli ----------
    if (!current || !current.root.contains(target)) return;
    const { root, picker } = current;

    const card = target.closest('[data-hx-color-card]');
    if (card instanceof HTMLButtonElement && !card.disabled) {
      const valueId = card.dataset.optionValueId;
      if (!valueId) return;
      markDrawer(root, valueId);
      markRow(picker, valueId, card.dataset.name || '');
      selectValue(picker, valueId);
      current.changed = true;
      return;
    }

    const viewSwitch = target.closest('[data-hx-color-view-switch]');
    if (viewSwitch instanceof HTMLElement) {
      showView(root, viewSwitch.dataset.hxColorViewSwitch === 'guide' ? 'guide' : 'colors');
      return;
    }

    if (target.closest('[data-hx-color-close]')) {
      // Sovituslinkki (data-slotti): Slotti-modaali avautuu omalla kuuntelijallaan, paneeli suljetaan
      closeDrawer(!target.closest('[data-slotti]'));
    }
  });

  // ---------- Hover-esikatselu ----------
  document.addEventListener('pointerover', (event) => {
    const swatch = event.target instanceof Element ? event.target.closest('[data-hx-color-swatch]') : null;
    if (!(swatch instanceof HTMLElement)) return;
    const label = pickerOf(swatch)?.querySelector('[data-hx-color-name]');
    if (label && swatch.dataset.name) label.textContent = swatch.dataset.name;
  });

  document.addEventListener('pointerout', (event) => {
    const grid = event.target instanceof Element ? event.target.closest('[data-hx-color-grid]') : null;
    if (!grid) return;
    const next = event.relatedTarget instanceof Element ? event.relatedTarget : null;
    if (next && grid.contains(next)) return;
    const label = pickerOf(grid)?.querySelector('[data-hx-color-name]');
    if (label instanceof HTMLElement) label.textContent = label.dataset.selected || '';
  });

  // ---------- Näppäimistö: Esc ja fokuksen pito paneelissa ----------
  document.addEventListener('keydown', (event) => {
    if (!current) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;

    const view = current.root.querySelector('[data-hx-color-view]:not([hidden])');
    if (!view) return;
    const items = Array.from(view.querySelectorAll(FOCUSABLE)).filter(
      (el) => el instanceof HTMLElement && el.offsetParent !== null
    );
    if (!items.length) return;
    const first = /** @type {HTMLElement} */ (items[0]);
    const last = /** @type {HTMLElement} */ (items[items.length - 1]);
    const active = document.activeElement;

    if (event.shiftKey && (active === first || !view.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !view.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  });
})();
